import { FC, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  InputError,
  Label,
  Textarea,
} from '@features/ui';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { UserGroupFormData } from '@src/types/userGroups';

interface UserGroupFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: UserGroupFormData) => void;
  initialData?: UserGroupFormData | null;
  mode: 'add' | 'edit';
}

const validationSchema = yup.object({
  name: yup.string().required('Name is required'),
  descriptions: yup.string().default(''),
  order: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? 0 : value))
    .min(0, 'Order must be a positive number')
    .required('Order is required'),
});

export const UserGroupFormDialog: FC<UserGroupFormDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
  initialData,
  mode,
}) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<UserGroupFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      descriptions: '',
      order: 0,
    },
  });

  useEffect(() => {
    if (open && initialData) {
      reset(initialData);
    } else if (open && !initialData) {
      reset({
        name: '',
        descriptions: '',
        order: 0,
      });
    }
  }, [open, initialData, reset]);

  const handleFormSubmit = (data: UserGroupFormData) => {
    onSubmit(data);
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[500px] max-w-[90vw] mx-auto">
        <DialogHeader className="mb-5">
          <DialogTitle className="text-2xl text-center mb-[23px]">
            {mode === 'add' ? 'Add User Group' : 'Edit User Group'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <div className="space-y-4">
            {/* Name */}
            <div>
              <Label className="text-sm mb-2">Name *</Label>
              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <Input
                    {...field}
                    error={!!errors.name}
                    placeholder="Enter group name"
                    textSize="sm"
                    variant="secondary"
                  />
                )}
              />
              {errors.name && <InputError>{errors.name.message}</InputError>}
            </div>

            {/* Description */}
            <div>
              <Label className="text-sm mb-2">Description</Label>
              <Controller
                control={control}
                name="descriptions"
                render={({ field }) => (
                  <Textarea
                    {...field}
                    hiddenHeader
                    className="min-h-[80px]"
                    placeholder="Enter group description (optional)"
                  />
                )}
              />
            </div>

            {/* Sort Order */}
            <div>
              <Label className="text-sm mb-2">Sort Order *</Label>
              <Controller
                control={control}
                name="order"
                render={({ field }) => (
                  <Input
                    {...field}
                    error={!!errors.order}
                    placeholder="0"
                    textSize="sm"
                    type="number"
                    variant="secondary"
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === '' ? '' : Number(e.target.value)
                      )
                    }
                  />
                )}
              />
              {errors.order && <InputError>{errors.order.message}</InputError>}
            </div>
          </div>

          <DialogFooter>
            <div className="flex items-center justify-center gap-10 mt-6">
              <Button className="w-20" size="sm" type="submit" variant="lime">
                {mode === 'add' ? 'Add' : 'Save'}
              </Button>
              <Button
                className="w-20"
                size="sm"
                type="button"
                variant="secondary"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
